/** @jsxImportSource @emotion/react */
import { useState, MouseEvent, useEffect, useCallback } from 'react'
import Popper from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import ButtonBase, { ButtonBaseProps } from '@mui/material/ButtonBase'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'
import { StaticDatePicker, StaticDateTimePicker } from '@mui/x-date-pickers'
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers'
import { format as formatDate } from 'date-fns-tz'
import IconButton from '@mui/material/IconButton'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import * as classes from './styles'

export const DEFAULT_FORMAT = 'PPP • p z'

type PickerType = 'date' | 'dateTime'

export type DateTimeInputProps = Omit<ButtonBaseProps, 'value' | 'onChange' | 'type'> & {
  value: Date | null
  onChange: (value: Date | null) => void
  format?: string
  withIconAdornment?: boolean
  withClearIcon?: boolean
  disabled?: boolean
  error?: boolean
  label?: string
  formHelperText?: string
  placeholder?: string
  size?: 'small' | 'medium' | 'large'
  fullWidth?: boolean
  className?: string
  pickerType?: PickerType
  disablePast?: boolean
  disableFuture?: boolean
  shouldDisableDate?: (date: Date) => boolean
  withConfirmation?: boolean
}

export function DateTimeInput({
  value,
  onChange,
  format = DEFAULT_FORMAT,
  withIconAdornment,
  withClearIcon,
  onClick,
  disabled,
  error,
  label,
  formHelperText,
  placeholder = '',
  size = 'large',
  fullWidth,
  className,
  pickerType = 'dateTime',
  disablePast,
  disableFuture,
  shouldDisableDate,
  withConfirmation = true,
  ...props
}: DateTimeInputProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [pendingValue, setPendingValue] = useState<Date | null>(value)

  useEffect(() => {
    setPendingValue(value)
  }, [value])

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
      onClick?.(event)
    },
    [onClick]
  )

  const close = useCallback(() => {
    if (anchorEl) {
      anchorEl.focus()
    }
    setAnchorEl(null)
  }, [anchorEl])

  const handleAccept = useCallback(
    (date: Date | null) => {
      onChange(date)
      close()
    },
    [onChange, close]
  )

  const handleClose = useCallback(() => {
    setPendingValue(value)
    close()
  }, [value, close])

  const open = Boolean(anchorEl)

  const controlButton = (
    <ButtonBase
      onClick={handleClick}
      disabled={disabled}
      css={classes.baseButton({ open, error, size })}
      disableRipple
      {...props}
    >
      {withIconAdornment && <CalendarIcon css={classes.adornmentIcon} />}
      {pendingValue ? (
        <span>{formatDate(pendingValue, format)}</span>
      ) : (
        <span className="placeholder">{placeholder}</span>
      )}
      {withClearIcon && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation()
            onChange(null)
          }}
          disabled={disabled}
        >
          <CrossIcon css={classes.clearIcon} />
        </IconButton>
      )}
    </ButtonBase>
  )

  const renderPicker = () => {
    const pickerProps = {
      value: pendingValue,
      onChange: withConfirmation ? setPendingValue : handleAccept,
      orientation: 'landscape' as const,
      onAccept: handleAccept,
      onClose: handleClose,
      disablePast,
      disableFuture,
      shouldDisableDate,
      slotProps: {
        actionBar: { actions: withConfirmation ? ['cancel' as const, 'accept' as const] : [] },
      },
    }

    if (pickerType === 'date') {
      return <StaticDatePicker {...pickerProps} />
    }

    return (
      <StaticDateTimePicker
        {...pickerProps}
        minutesStep={1}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
        }}
      />
    )
  }

  return (
    <div className={className} css={classes.wrapper(fullWidth)}>
      <div className="base">
        {label ? (
          <FormControlLabel control={controlButton} label={label} labelPlacement="top" css={classes.labelWrapper} />
        ) : (
          controlButton
        )}
        {!!formHelperText && <FormHelperText error={error}>{formHelperText}</FormHelperText>}
      </div>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        css={classes.popper}
        className="popper"
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ],
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>{renderPicker()}</div>
        </ClickAwayListener>
      </Popper>
    </div>
  )
}
